import { useCallback, useMemo } from "preact/hooks";
import { useComputed, useSignal } from "@preact/signals";
import { endOfDay, isSameDay, startOfDay } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useApp } from "~/core";
import { useAppTime } from "~/hooks";
import { SingleWidgetProps } from "../interfaces";

const SERVICE = "outlook" as const;
const ONE_DAY_IN_MS = 1 * 24 * 60 * 60 * 1000;

const useWidget = ({}: SingleWidgetProps) => {
    const {
        api: { time, account },
        settings,
        mobile,
    } = useApp();
    const navigate = useNavigate();
    const outlookInstance = account.instance(SERVICE);
    const selectedDay = useSignal(new Date());
    const selectedRange = useComputed(() => {
        const day = selectedDay.value;

        return {
            start: startOfDay(day),
            end: endOfDay(day),
        };
    });
    const loading = time.external.loading(selectedRange.value);
    const externals = useComputed(() => {
        const day = selectedDay.value;
        const externals = time.external.externals.value;
        const selected = settings.selectedCalendars.value[SERVICE] ?? [];

        return externals
            .filter(
                (external) =>
                    external.service === SERVICE &&
                    selected.includes(external.calendar_id) &&
                    isSameDay(external.start, day),
            )
            .sort((a, b) => a.start - b.start);
    });

    const now = useAppTime("minute");
    const upcomingId = useMemo(() => {
        const event = externals.value.find(
            (item) =>
                item.stop >= now.value.getTime() &&
                item.stop - item.start < ONE_DAY_IN_MS,
        );

        if (event) {
            return event.id;
        }

        return externals.value.find((item) => item.stop >= now.value.getTime())
            ?.id;
    }, [externals.value, now.value]);
    const isLoading = outlookInstance.value.loading || loading.value;
    const isConnectCTA =
        !isLoading &&
        !outlookInstance.value.active &&
        externals.value.length === 0;
    const isNotSynced = !isLoading && !outlookInstance.value.loaded;
    const canGoNow = !isSameDay(selectedDay.value, now.value);

    const handleMoveDay = useCallback(
        (shift: number) => {
            const newDate = new Date(selectedDay.peek());

            newDate.setDate(newDate.getDate() + shift);
            selectedDay.value = newDate;
            mobile.impact("light");
        },
        [mobile, selectedDay],
    );

    const handlePrevious = useCallback(
        () => handleMoveDay(-1),
        [handleMoveDay],
    );

    const handleNext = useCallback(() => handleMoveDay(1), [handleMoveDay]);

    const handleNow = useCallback(() => {
        if (!isSameDay(selectedDay.peek(), now.peek())) {
            selectedDay.value = new Date();
            mobile.impact("light");
        }
    }, [mobile, selectedDay, now]);

    return {
        isLoading,
        isConnectCTA,
        isNotSynced,
        canGoNow,
        now,
        externals,
        selectedDay,
        upcomingId,
        navigate,
        handlePrevious,
        handleNext,
        handleNow,
    };
};

export default useWidget;
